import { LoaderRow } from "@circle-react-shared/LoaderRow";
import { Link } from "@circleco/compass/components/Link";

import { t } from "@/i18n-js/instance";
import { useCommentsContainer } from "@/react/components/CommentsCompass/CommentsContainer";
import { useCommentList } from "@/react/components/CommentsCompass/data/useCommentList";
import { useCommentsConfig } from "@/react/components/CommentsCompass/Provider/ConfigContext";
import type { Comment as CommentType } from "@/react/types/Comment";

import { Comment } from "./Comment";
import { ReplyComposer } from "./ReplyComposer";
import { useCommentsThread } from "./ThreadContext";

interface RepliesListProps {
  allRepliesPreloaded?: boolean;
  parentComment: CommentType;
}

export const RepliesList = ({
  parentComment,
  allRepliesPreloaded = false,
}: RepliesListProps) => {
  const { postId, canCompose } = useCommentsConfig();
  const { isNarrow } = useCommentsContainer();
  const { isReplyFormOpen } = useCommentsThread();

  const { comments, hasNextPage, fetchNextPage, isFetching } = useCommentList({
    postId,
    parentCommentId: parentComment.id,
    preloaded: {
      comments: parentComment.replies ?? [],
      totalCount: parentComment.replies_count,
      // Workaround: replies_count overcounts (the backend counts replies that
      // policies later filter out), so inferring has_next_page from it would
      // show a bogus "Show more replies" when the full set is already loaded.
      ...(allRepliesPreloaded && { hasNextPage: false }),
    },
  });

  const shouldShowForm = canCompose && isReplyFormOpen;

  return (
    <div className="col-span-4 grid grid-cols-subgrid gap-y-1">
      {comments.map(comment => (
        <Comment key={comment.id} comment={comment} indented />
      ))}
      {isFetching && (
        <div className="col-start-2 col-span-3">
          <LoaderRow />
        </div>
      )}
      {!isFetching && hasNextPage && (
        <div className="col-start-2 col-span-3 pt-1.5 pb-2.5 text-left">
          <Link
            asButton
            variant="custom"
            typography="body-sm"
            color="secondary"
            hoverColor="primary"
            onClick={event => {
              event.preventDefault();
              void fetchNextPage();
            }}
          >
            {t("comments.list.show_more_replies")}
          </Link>
        </div>
      )}
      {shouldShowForm && (
        <div className={isNarrow ? "col-span-4" : "col-start-2 col-span-3"}>
          <ReplyComposer parentComment={parentComment} />
        </div>
      )}
    </div>
  );
};
